<template>
    <div class="automation-run-detail">
        <div class="container-xl">
            <div class="card page-card automation-wizard-card">
                <div class="card-header d-flex align-items-center">
                    <div class="d-flex align-items-center">
                        <span class="automation-wizard-card__icon"><i class="far fa-fw fa-history"></i></span>
                        <div class="title">{{ run.title }}</div>
                    </div>
                </div>

                <!-- what was run, by whom and when -->
                <div class="automation-run-detail__meta">
                    <div class="automation-run-detail__meta-item">
                        <div class="automation-run-detail__meta-label">{{ trans('inputs') }}</div>
                        <div class="automation-run-detail__meta-value">{{ run.inputs }}</div>
                    </div>
                    <div class="automation-run-detail__meta-item">
                        <div class="automation-run-detail__meta-label">{{ trans('user') }}</div>
                        <div class="automation-run-detail__meta-value">{{ run.user || run.trigger_label }}</div>
                    </div>
                    <div class="automation-run-detail__meta-item">
                        <div class="automation-run-detail__meta-label">{{ trans('created_at') }}</div>
                        <div class="automation-run-detail__meta-value">{{ run.created_at }}</div>
                    </div>
                    <div class="automation-run-detail__meta-item">
                        <div class="automation-run-detail__meta-label">{{ trans('finished_at') }}</div>
                        <div class="automation-run-detail__meta-value">{{ run.finished_at || '-' }}</div>
                    </div>
                    <span class="automation-run-detail__status" :class="'is-' + run.status">{{ run.status_label }}</span>
                </div>

                <div class="card-block">
                    <div class="automation-result__stats">
                        <div class="automation-result__stat is-success">
                            <span class="automation-result__stat-icon"><i class="fa fa-check"></i></span>
                            <div class="automation-result__stat-main">
                                <div class="automation-result__stat-label">{{ trans('success') }}</div>
                                <div class="automation-result__stat-value">{{ writtenItems.length }}</div>
                            </div>
                            <div class="automation-result__stat-side" v-if="run.success_total">
                                <div class="automation-result__stat-label">{{ trans('written') }}</div>
                                <div class="automation-result__stat-value is-total">{{ run.success_total }}</div>
                            </div>
                        </div>
                        <div class="automation-result__stat" :class="failedItems.length ? 'is-failed' : 'is-neutral'">
                            <span class="automation-result__stat-icon"><i class="fa fa-times"></i></span>
                            <div class="automation-result__stat-main">
                                <div class="automation-result__stat-label">{{ trans('failed') }}</div>
                                <div class="automation-result__stat-value">{{ failedItems.length }}</div>
                            </div>
                        </div>
                        <div class="automation-result__stat is-unchanged">
                            <span class="automation-result__stat-icon"><i class="far fa-equals"></i></span>
                            <div class="automation-result__stat-main">
                                <div class="automation-result__stat-label">{{ trans('unchanged') }}</div>
                                <div class="automation-result__stat-value">{{ unchangedItems.length }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="automation-preview__section" v-for="section in sections" :key="section.name">
                        <h6 class="automation-preview__heading" :class="'is-' + section.name">
                            <i :class="section.icon"></i> {{ section.label }}
                            <span class="automation-preview__count">{{ section.items.length }}</span>
                            <button type="button" class="automation-preview__toggle" v-if="section.collapsible" @click="showUnchanged = !showUnchanged">
                                {{ showUnchanged ? trans('hide') : trans('show') }}
                                <i class="far fa-chevron-down ml-1" :class="{'fa-rotate-180': showUnchanged}"></i>
                            </button>
                        </h6>

                        <ul class="automation-changes" v-if="!section.collapsible || showUnchanged">
                            <li class="automation-change" :class="'is-' + section.name" v-for="item in section.items" :key="item.key">
                                <span class="automation-change__avatar">{{ initials(item) }}</span>

                                <div class="automation-change__subject">
                                    <div class="automation-change__title">{{ title(item) }}</div>
                                    <div class="automation-change__reason" v-if="section.name === 'failed'">
                                        <i class="far fa-exclamation-triangle"></i> {{ item.error }}
                                    </div>
                                    <div class="automation-change__badges" v-else-if="item.columns">
                                        <span class="automation-change__badge" v-for="column in badgeColumns" :key="column.key"
                                            :class="{'is-highlighted': column.highlighted_by && item.columns[column.highlighted_by]}">
                                            <template v-if="column.display === 'labeled-badge'">{{ column.label }}: </template>{{ item.columns[column.key] }}
                                        </span>
                                    </div>
                                    <div class="automation-change__badges" v-else-if="labelBadges(item).length">
                                        <span class="automation-change__badge" v-for="(badge, index) in labelBadges(item)" :key="index">{{ badge }}</span>
                                    </div>
                                </div>

                                <div class="automation-change__progress" v-for="column in (item.columns ? progressColumns : [])" :key="column.key">
                                    <div class="automation-change__progress-label">
                                        <span>{{ column.label }}</span>
                                        <b>{{ item.columns[column.key] }}</b>
                                    </div>
                                    <div class="automation-change__bar" :class="{'is-empty': progress(item.columns[column.key]) === null}">
                                        <span :style="{width: (progress(item.columns[column.key]) || 0) + '%'}"></span>
                                    </div>
                                </div>

                                <div class="automation-change__values" v-if="section.name === 'failed'">
                                    <span class="automation-change__not-written" v-if="item.new_value !== item.old_value">{{ item.new_value }} {{ trans('not_written') }}</span>
                                </div>
                                <div class="automation-change__values" v-else-if="section.name === 'written'">
                                    <span class="automation-change__old">{{ item.old_value }}</span>
                                    <i class="far fa-long-arrow-right"></i>
                                    <span class="automation-change__new">{{ item.new_value }}</span>
                                </div>
                                <div class="automation-change__values" v-else>
                                    <span class="automation-change__same">{{ item.old_value }}</span>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                <!-- the row with the buttons like in the wizard, pinned to the bottom of the window when out of view -->
                <div class="automation-run-footer-holder" ref="footerHolder">
                    <div class="automation-run-footer" :class="{'is-pinned': footerPinned}" :style="footerStyle">
                        <span class="automation-run-footer__total">{{ trans('total') }} <b>{{ run.items.length }}</b></span>

                        <a :href="historyUrl" class="btn btn-secondary"><i class="fa fa-arrow-left"></i> {{ trans('back') }}</a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import TranslationsUtils from "../app-components/TranslationsUtils";
    import AutomationRunItems from "./AutomationRunItems";

    export default {
        mixins: [TranslationsUtils, AutomationRunItems],
        props: {
            run: { type: Object },
            historyUrl: { type: String }
        },

        computed: {
            // what was not written (with the reason), what was written and what stayed as it was
            sections() {
                return [
                    { name: 'failed', label: this.trans('failed_written'), icon: 'far fa-exclamation-circle', items: this.failedItems },
                    { name: 'written', label: this.trans('written'), icon: 'far fa-check', items: this.writtenItems },
                    { name: 'unchanged', label: this.trans('unchanged'), icon: 'far fa-equals', items: this.unchangedItems, collapsible: true }
                ].filter(section => section.items.length);
            }
        }
    };
</script>



// WEBPACK FOOTER //
// resources/assets/admin/js/components/AutomationRunDetail.vue